import { Avatar, Button, Icons, OpennessMeter } from "@jod/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useT } from "../features/i18n/index.ts";
import { useLiff } from "../features/liff/use-liff.ts";
import { useSession } from "../features/session/session-context.tsx";
import { getMode, TONIGHT_MODES, type TonightMode } from "../features/tonight/mode.ts";

export const Route = createFileRoute("/tonight-mode")({
  component: TonightModeScreen,
});

function TonightModeScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { profile } = useLiff();
  const { setMode, tableCode } = useSession();
  const [selected, setSelected] = useState<TonightMode>("private");
  const meta = getMode(selected);
  const hidden = selected === "private";

  const commit = () => {
    setMode(selected);
    navigate({ to: "/home" });
  };

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col px-[18px] py-8">
      <div className="space-y-1.5 text-center">
        <div className="jod-kicker">{t("tonightMode.kicker")}</div>
        <h1 className="h-display text-[1.7rem]">{t("tonightMode.title")}</h1>
      </div>

      {/* live "what others see" preview — makes consent tangible */}
      <div className="glass-hi relative mt-5 overflow-hidden rounded-xl p-4">
        <div className="jod-sheen" aria-hidden="true" />
        <div className="jod-kicker relative mb-3">
          {hidden ? t("tonightMode.previewHiddenKicker") : t("tonightMode.previewVisibleKicker")}
        </div>
        <div className="relative flex items-center gap-3">
          <Avatar
            label={profile?.displayName ?? t("common.you")}
            glyph="🌙"
            size={48}
            ghosted={hidden}
            ring={!hidden}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 font-display font-semibold text-cream">
              {hidden ? (
                <>
                  <Icons.IconLock size={15} /> {t("tonightMode.hidden")}
                </>
              ) : (
                (profile?.displayName ?? t("common.you"))
              )}
            </div>
            <div className="text-cream-3 text-xs">
              {hidden
                ? t("tonightMode.hiddenSub")
                : t("tonightMode.tableAndMode", { table: tableCode, mode: meta.label })}
            </div>
          </div>
        </div>
        <p className="relative mt-3 text-cream-2 text-sm">{t(meta.previewKey)}</p>
      </div>

      {/* mode spectrum */}
      <div className="mt-5 space-y-2.5">
        {TONIGHT_MODES.map((m) => {
          const on = selected === m.key;
          const onClasses = on
            ? m.tone === "lime"
              ? "border-[oklch(86%_0.18_120_/_45%)] bg-[oklch(86%_0.18_120_/_8%)]"
              : "border-[oklch(79%_0.12_80_/_50%)] bg-[oklch(79%_0.12_80_/_8%)]"
            : "border-hairline bg-[oklch(100%_0_0_/_3%)]";
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => setSelected(m.key)}
              aria-pressed={on}
              className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3.5 text-left transition-all ${onClasses}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-display font-semibold text-cream">{t(m.titleKey)}</span>
                  <OpennessMeter level={m.openness} tone={m.tone} />
                </div>
                <div className="mt-0.5 text-cream-3 text-xs">{t(m.descKey)}</div>
              </div>
              {on ? (
                <span className={m.tone === "lime" ? "text-lime" : "text-gold"}>
                  <Icons.IconCheck size={20} />
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <Button size="lg" className="mt-auto w-full" onClick={commit}>
        {t("tonightMode.enter")}
      </Button>
    </div>
  );
}
